import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Feature, FeatureCollection } from 'geojson';
import { featureCollection } from '@turf/turf';
import landAdvisorModes from '../constants/landAdvisorMode';
import useSoilAttributes, {
  initialState as initialSoilAttributesState,
  SoilAttributesState,
} from '../hooks/useSoilAttributes';

type LandAdvisorMode = 'view' | 'draw' | 'analysis';

interface LandAdvisorState {
  mode: LandAdvisorMode;
  setMode: (mode: LandAdvisorMode) => void;
  parcels: Feature[];
  setParcels: (parcels: Feature[]) => void;
  soilAttributes: SoilAttributesState;
  soilPolygons: FeatureCollection;
  setSoilPolygons: (soilPolygons: FeatureCollection) => void;
  resetState: () => void;
}

const initialState = {
  mode: landAdvisorModes.VIEW,
  setMode: () => {},
  parcels: [],
  setParcels: () => {},
  soilAttributes: initialSoilAttributesState,
  soilPolygons: featureCollection([]),
  setSoilPolygons: () => {},
  resetState: () => {},
};

export const LandAdvisorContext = createContext<LandAdvisorState>(initialState);

export const LandAdvisorContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [mode, setMode] = useState<LandAdvisorMode>(landAdvisorModes.VIEW);
  const [parcels, setParcels] = useState<Feature[]>([]);
  const [soilPolygons, setSoilPolygons] = useState<FeatureCollection>(
    featureCollection([]),
  );

  const soilAttributes = useSoilAttributes({ data: soilPolygons });
  const { resetSoilAttributes } = soilAttributes;

  const resetState = useCallback(() => {
    setMode(landAdvisorModes.VIEW);
    setParcels([]);
    setSoilPolygons(featureCollection([]));
    resetSoilAttributes();
  }, [resetSoilAttributes]);

  const value = useMemo(
    () => ({
      mode,
      setMode,
      parcels,
      setParcels,
      soilAttributes,
      soilPolygons,
      setSoilPolygons,
      resetState,
    }),
    [
      mode,
      setMode,
      parcels,
      setParcels,
      soilAttributes,
      soilPolygons,
      setSoilPolygons,
      resetState,
    ],
  );

  return (
    <LandAdvisorContext.Provider value={value}>
      {children}
    </LandAdvisorContext.Provider>
  );
};

export const useLandAdvisorContext = () => useContext(LandAdvisorContext);
